import React, { useState } from "react";
import dayjs from "dayjs";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
} from "@mui/material";
import ErrorHelper from "../../../../../../components/ErrorWrapper";
import Flex from "../../../../../../components/Flex";
import Input from "../../../../../../components/Input";
import Text from "../../../../../../components/Text";
import SignatureInput from "../../../../../../components/signiture/SignatureInput";
import { useSignatureController } from "../../../../../../components/signiture/use-signature-controller";
import { useQueryParams } from "../../../../../../hooks/use-query-param";

interface ReleaseOfInformationFormProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  touched: any;
  errors: any;
  viewOnly?: boolean;
}

const ReleaseOfInformationForm = ({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  touched: _touched,
  errors: _errors,
  viewOnly,
}: ReleaseOfInformationFormProps) => {
  const [_k, setK] = useState<boolean>(true);
  const { canvasRef, signPad, isSignEmpty } = useSignatureController(
    _k,
    values?.releaseOfInformation?.sign
  );
  const practiceName = useQueryParams("practiceName");
  const patientName = useQueryParams("name");
  const expiryDate = dayjs().add(1, "year").format("MMMM D, YYYY");
  const authorizationDate = dayjs().format("MMMM D, YYYY");
  const signedByName =
    values?.releaseOfInformation?.signedByName || patientName;

  return (
    <Box>
      <Text variant="subtitle1" fontWeight="500" mb={1}>
        Preview: Release of Information
      </Text>

      {/* Person or Organization block */}
      <Box sx={{ mb: 3 }}>
        <Text variant="subtitle2" fontWeight="500" mb={1}>
          Person or Organization with Whom Information can be Shared
        </Text>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Input
              label="Recipient Name / Organization:"
              textFieldProps={{
                name: "releaseOfInformation.recipientName",
                value: values?.releaseOfInformation?.recipientName || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Dr. John Smith",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              label="Address:"
              textFieldProps={{
                name: "releaseOfInformation.recipientAddress",
                value: values?.releaseOfInformation?.recipientAddress || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "321 Main Street, Anytown, PA 12345",
                disabled: viewOnly,
              }}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Types of Information to be Shared */}
      <Box sx={{ mb: 3 }}>
        <Text variant="subtitle2" fontWeight="500" mb={0.5}>
          Types of Information to be Shared
        </Text>
        <Flex flexDirection="column" gap={0.5}>
          <FormControlLabel
            disabled={viewOnly}
            control={
              <Checkbox
                disabled={viewOnly}
                checked={
                  values?.releaseOfInformation?.infoTypes?.entireRecord || false
                }
                onChange={(e) =>
                  setFieldValue(
                    "releaseOfInformation.infoTypes.entireRecord",
                    e.target.checked
                  )
                }
                name="releaseOfInformation.infoTypes.entireRecord"
              />
            }
            label="Entire treatment record"
          />
          <FormControlLabel
            disabled={viewOnly}
            control={
              <Checkbox
                disabled={viewOnly}
                checked={
                  values?.releaseOfInformation?.infoTypes
                    ?.currentStatusLocation || false
                }
                onChange={(e) =>
                  setFieldValue(
                    "releaseOfInformation.infoTypes.currentStatusLocation",
                    e.target.checked
                  )
                }
                name="releaseOfInformation.infoTypes.currentStatusLocation"
              />
            }
            label="Current status and location"
          />
          <FormControlLabel
            disabled={viewOnly}
            control={
              <Checkbox
                disabled={viewOnly}
                checked={
                  values?.releaseOfInformation?.infoTypes?.billingStatements ||
                  false
                }
                onChange={(e) =>
                  setFieldValue(
                    "releaseOfInformation.infoTypes.billingStatements",
                    e.target.checked
                  )
                }
                name="releaseOfInformation.infoTypes.billingStatements"
              />
            }
            label="Billing statements"
          />
          <FormControlLabel
            disabled={viewOnly}
            control={
              <Checkbox
                disabled={viewOnly}
                checked={
                  values?.releaseOfInformation?.infoTypes?.other || false
                }
                onChange={(e) =>
                  setFieldValue(
                    "releaseOfInformation.infoTypes.other",
                    e.target.checked
                  )
                }
                name="releaseOfInformation.infoTypes.other"
              />
            }
            label="Other"
          />
        </Flex>
      </Box>

      {/* Purpose of Disclosure */}
      <Box sx={{ mb: 3 }}>
        <Text variant="subtitle2" fontWeight="500" mb={0.5}>
          Purpose of Disclosure
        </Text>
        <Flex flexDirection="column" gap={0.5}>
          <FormControlLabel
            disabled={viewOnly}
            control={
              <Checkbox
                disabled={viewOnly}
                checked={
                  values?.releaseOfInformation?.purposes?.continuityOfCare ||
                  false
                }
                onChange={(e) =>
                  setFieldValue(
                    "releaseOfInformation.purposes.continuityOfCare",
                    e.target.checked
                  )
                }
                name="releaseOfInformation.purposes.continuityOfCare"
              />
            }
            label="Continuity of care"
          />
          <FormControlLabel
            disabled={viewOnly}
            control={
              <Checkbox
                disabled={viewOnly}
                checked={
                  values?.releaseOfInformation?.purposes?.emergencyManagement ||
                  false
                }
                onChange={(e) =>
                  setFieldValue(
                    "releaseOfInformation.purposes.emergencyManagement",
                    e.target.checked
                  )
                }
                name="releaseOfInformation.purposes.emergencyManagement"
              />
            }
            label="Emergency management"
          />
          <FormControlLabel
            disabled={viewOnly}
            control={
              <Checkbox
                disabled={viewOnly}
                checked={
                  values?.releaseOfInformation?.purposes?.accountManagement ||
                  false
                }
                onChange={(e) =>
                  setFieldValue(
                    "releaseOfInformation.purposes.accountManagement",
                    e.target.checked
                  )
                }
                name="releaseOfInformation.purposes.accountManagement"
              />
            }
            label="Account management"
          />
          <FormControlLabel
            disabled={viewOnly}
            control={
              <Checkbox
                disabled={viewOnly}
                checked={values?.releaseOfInformation?.purposes?.other || false}
                onChange={(e) =>
                  setFieldValue(
                    "releaseOfInformation.purposes.other",
                    e.target.checked
                  )
                }
                name="releaseOfInformation.purposes.other"
              />
            }
            label="Other"
          />
        </Flex>
      </Box>

      {/* Duration and Revocation of Authorization */}
      <Box sx={{ mb: 3 }}>
        <Text variant="subtitle2" fontWeight="500" mb={0.5}>
          Duration and Revocation of Authorization
        </Text>
        <Text variant="body2" mb={1}>
          This authorization is good for a period of <em>1 year</em>. It will
          expire on <strong>{expiryDate}</strong>.
        </Text>
        <Box sx={{ pl: 2, color: "text.secondary", fontSize: "0.85rem" }}>
          <ul>
            <li>
              I understand that I can revoke this authorization at any time
              prior to that date by contacting the practice in writing.
            </li>
            <li>
              I understand that if the practice has already shared the
              information authorized here at the time I revoke this
              authorization, then it is too late to prevent that information
              from being shared.
            </li>
            <li>
              I understand that the practice cannot make completion of this
              authorization a condition for any treatments or benefits I am
              entitled to, unless this authorization is necessary to determine
              eligibility for treatment or benefits or to pay for treatments I
              receive.
            </li>
          </ul>
        </Box>
      </Box>

      {/* Authorization Statement & Patient Signature Block */}
      <Box sx={{ mb: 3 }}>
        <Text variant="subtitle2" fontWeight="500" mb={0.5}>
          Authorization
        </Text>
        <Text variant="body2" color="textSecondary" mb={2}>
          I hereby authorize {practiceName} to release information as described
          above to, and request information from, the person or organization
          identified herein. I understand that the person or organization named
          above may not be subject to the same privacy laws and regulations as{" "}
          {practiceName} and may be able to further share the information
          disclosed under this authorization. I consent to use electronic
          signatures, and my signature below is the same as a handwritten
          signature for the purposes of validity, enforceability, and
          admissibility.
        </Text>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Input
              label="Signed By:"
              textFieldProps={{
                name: "releaseOfInformation.signedByName",
                value: values?.releaseOfInformation?.signedByName || "",
                onChange: handleChange,
                onBlur: handleBlur,
                placeholder: "Enter Your Legal Name",
                disabled: viewOnly,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Input
              label="Relationship:"
              textFieldProps={{
                select: true,
                SelectProps: { native: true },
                name: "releaseOfInformation.relationship",
                value: values?.releaseOfInformation?.relationship || "",
                onChange: handleChange,
                onBlur: handleBlur,
                disabled: viewOnly,
                children: (
                  <>
                    <option value="">-- Select Relationship --</option>
                    <option value="Self/Patient">Self/Patient</option>
                    <option value="Authorized Representative">
                      Authorized Representative
                    </option>
                  </>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12}>
            {viewOnly || values?.releaseOfInformation?.sign ? (
              <ErrorHelper label="Signature">
                <Box
                  style={{
                    position: "relative",
                    borderWidth: 1,
                    borderRadius: 5,
                    borderStyle: "solid",
                    width: "368px",
                    height: "100%",
                  }}
                >
                  <img
                    src={values?.releaseOfInformation?.sign}
                    alt="Release of Information signature"
                    style={{ height: "150px", width: "360px" }}
                  />

                  {!viewOnly && (
                    <IconButton
                      color="error"
                      onClick={() => {
                        setK((prev) => !prev);
                        signPad?.clear();
                        setFieldValue("releaseOfInformation.sign", undefined);
                      }}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -20,
                        height: "10px",
                      }}
                    >
                      <HighlightOffIcon color="error" fontSize="medium" />
                    </IconButton>
                  )}
                </Box>
              </ErrorHelper>
            ) : (
              <Box
                width={"368px"}
                position={"relative"}
                onMouseLeave={() => {
                  if (!isSignEmpty() && signPad?.toDataURL()) {
                    setFieldValue(
                      "releaseOfInformation.sign",
                      signPad?.toDataURL()
                    );
                  }
                }}
              >
                <SignatureInput
                  label="Signature"
                  canvasRef={canvasRef}
                  w="368px"
                  h="100px"
                />

                <Button
                  variant="contained"
                  sx={{
                    position: "absolute",
                    bottom: 10,
                    left: "300px",
                    height: "24px",
                  }}
                  onClick={() => {
                    setK((prev) => !prev);
                    signPad?.clear();
                  }}
                >
                  clear
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Practice Acknowledgment Panel Footer */}
      <Box
        sx={{
          p: 2,
          border: "1px solid #dcdcdc",
          borderRadius: "4px",
          backgroundColor: "#f9fff0",
        }}
      >
        <Text variant="subtitle2" fontWeight="500" mb={1}>
          Practice Acknowledgment
        </Text>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <FormControlLabel
              disabled={viewOnly}
              control={
                <Checkbox
                  disabled={viewOnly}
                  checked={
                    values?.releaseOfInformation?.practiceAckChecked || false
                  }
                  onChange={(e) =>
                    setFieldValue(
                      "releaseOfInformation.practiceAckChecked",
                      e.target.checked
                    )
                  }
                  name="releaseOfInformation.practiceAckChecked"
                />
              }
              label={`Sign This Form: I, ${
                signedByName || "________"
              }, have signed this authorization on ${authorizationDate}.`}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default ReleaseOfInformationForm;
